/**
 * O fluxo de codigo de autorizacao com PKCE S256, contra o Keycloak.
 *
 * docs/AUTENTICACAO-E-ACESSO.md, secao 2. Em ordem:
 *
 *   1. a aplicacao guarda o verificador e para onde voltar, e manda o navegador
 *      ao Keycloak pedindo nivel 2 de autenticacao;
 *   2. o Keycloak pede senha e segundo fator;
 *   3. o navegador volta para /login com um codigo;
 *   4. a aplicacao troca o codigo por token, provando posse com o verificador.
 *
 * Nada aqui escreve token em armazenamento do navegador. Ver armazem-token.ts.
 */

import {
  definirSessao,
  limparSessao,
  sessaoAtiva,
  sessaoDaResposta,
  type SessaoToken,
} from './armazem-token';
import { ESCOPOS, lerConfiguracao, NIVEL_SEGUNDO_FATOR } from './configuracao';
import {
  consumirPedido,
  descartarPedido,
  guardarPedido,
  guardarResultadoDaAcao,
  limparTentativaSilenciosa,
  marcarSessaoConhecida,
  marcarTentativaSilenciosa,
} from './deposito-pedido';
import { caminhoInternoSeguro } from './destino';
import { MENSAGEM_GENERICA, mensagemDoCodigoOauth, MENSAGEM_SEM_RESPOSTA } from './mensagens';
import { gerarDesafioPkce, gerarValorOpaco } from './pkce';

/**
 * Falha com frase pronta para a tela.
 *
 * O detalhe tecnico fica em `causa` e vai para o console — nunca para o
 * usuario (CLAUDE.md, "Regras de interface", item 8).
 */
export class FalhaAutenticacao extends Error {
  constructor(
    readonly aviso: string,
    readonly causa?: unknown,
  ) {
    super(aviso);
    this.name = 'FalhaAutenticacao';
  }
}

export interface RetornoDeAutorizacao {
  codigo: string | null;
  estado: string | null;
  erro: string | null;
}

/** Le a volta do Keycloak na busca da URL. Devolve nulo quando nao ha volta. */
export function lerRetorno(busca: string): RetornoDeAutorizacao | null {
  const parametros = new URLSearchParams(busca);
  const codigo = parametros.get('code');
  const erro = parametros.get('error');

  if (!codigo && !erro) {
    return null;
  }

  return { codigo, estado: parametros.get('state'), erro };
}

/**
 * Manda o navegador ao Keycloak.
 *
 * `silencioso` acrescenta `prompt=none`: o Keycloak decide sem mostrar tela
 * nenhuma — devolve um codigo se a sessao de identidade dele ainda vale, ou
 * `error=login_required` se nao vale. E assim que recarregar a pagina nao
 * derruba a sessao, mesmo com o token vivendo so em memoria.
 *
 * Por que redirecionamento de topo, e nao um quadro escondido: o cookie de
 * identidade do Keycloak e `SameSite=None; Secure`, e quadro de terceira parte
 * depende de o navegador aceitar cookie de terceira parte — o que Chrome esta
 * justamente encerrando. Navegacao de topo nao depende disso.
 */
export async function iniciarAutorizacao(opcoes: {
  destino: string;
  silencioso?: boolean;
  /**
   * Alias do provedor federado no realm (ex.: `govbr`). Vira `kc_idp_hint` na
   * URL de autorizacao e leva a pessoa direto para a tela do provedor, sem
   * passar pela tela de senha do Keycloak. Sem dica, o comportamento de hoje
   * nao muda.
   */
  dicaProvedor?: string;
  /** Ver o comentario em `AcaoNoKeycloak`. */
  acaoNoKeycloak?: AcaoNoKeycloak;
}): Promise<void> {
  const { endereco } = await montarPedido(opcoes);

  if (opcoes.silencioso) {
    marcarTentativaSilenciosa();
    // Nao suja o historico: a tentativa silenciosa nao e navegacao do usuario.
    window.location.replace(endereco);
    return;
  }

  window.location.assign(endereco);
}

/**
 * O tema em vigor nesta aba: `dark` ou `light`.
 *
 * Viaja na URL de autorizacao, como `teg_tema`, para que a tela do Keycloak
 * abra no mesmo tema desta. Nao ha como ela descobrir sozinha: a escolha mora
 * no `localStorage` DESTA origem, e `localStorage` nao atravessa origem — em
 * DSV e HML o sistema esta na Vercel e o Keycloak no Render, e em
 * desenvolvimento sao duas portas do localhost (conferido em 2026-09-14).
 * Sem o parametro, a tela de identificacao cai na preferencia do sistema
 * operacional e contraria quem escolheu o contrario dela.
 *
 * O Keycloak ignora parametro que nao conhece, entao isto nao altera o fluxo
 * de autorizacao: quem le e o tema `teg`, no primeiro carregamento
 * (infra/keycloak/tema/teg/login/template.ftl).
 *
 * Le a CLASSE do `<html>`, e nao a chave `teg-theme`: a classe e o tema
 * RESOLVIDO. Com `defaultTheme="system"` a chave pode dizer "system", e o que
 * precisa atravessar e o resultado, nao a regra.
 */
export function temaEmVigor(): 'dark' | 'light' {
  if (typeof document === 'undefined') {
    return 'light';
  }

  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/**
 * As escolhas do widget de acessibilidade que estao valendo nesta aba.
 *
 * Viajam na URL de autorizacao, como `teg_a11y`, pela mesma razao de
 * `temaEmVigor` — e com um motivo a mais: isto nao e gosto. Quem depende de
 * alto contraste e o perde justamente na tela onde digita a senha encontra uma
 * barreira de acesso, e acessibilidade em sistema publico e obrigacao
 * (docs/LGPD-E-ACESSIBILIDADE.md).
 *
 * Le as CLASSES do `<html>`, e nao a chave `teg-a11y-prefs`, pela mesma razao
 * de `temaEmVigor`: o que atravessa e o estado em vigor, nao a regra que o
 * produziu. Os nomes curtos sao os que o tema conhece — lista fechada dos dois
 * lados (infra/keycloak/tema/teg/login/template.ftl).
 */
const CLASSES_A11Y: ReadonlyArray<readonly [string, string]> = [
  ['a11y-high-contrast', 'contraste'],
  ['a11y-font-lg', 'fonte-lg'],
  ['a11y-font-xl', 'fonte-xl'],
  ['a11y-underline-links', 'sublinhado'],
  ['a11y-reduce-motion', 'movimento'],
];

export function acessibilidadeEmVigor(): string {
  if (typeof document === 'undefined') {
    return '';
  }

  const { classList } = document.documentElement;

  return CLASSES_A11Y.filter(([classe]) => classList.contains(classe))
    .map(([, nome]) => nome)
    .join(',');
}

/**
 * Acao que a aplicacao pede ao Keycloak para a pessoa cumprir agora (acao
 * iniciada pela aplicacao, `kc_action`). Lista fechada: so as trocas de
 * segundo fator da tela "Meu perfil".
 *
 * A troca acontece no Keycloak, e so la: ele confirma que a sessao e recente
 * (ou pede a entrada de novo), confere o metodo novo — um codigo do aparelho
 * novo, ou um codigo que chegou ao e-mail — e so entao grava. A aplicacao nao
 * tem rota que grave segundo fator. Na volta, o pedido e concluido como uma
 * entrada comum e a pessoa cai de novo no destino guardado.
 */
export type AcaoNoKeycloak = 'CONFIGURE_TOTP' | 'teg-cadastrar-codigo-email';

/**
 * Monta o pedido e guarda o que precisa atravessar o redirecionamento.
 *
 * Separado de `iniciarAutorizacao` porque o endereco montado e o que se pode
 * conferir em teste — navegacao de verdade, nao.
 */
export async function montarPedido(opcoes: {
  destino: string;
  silencioso?: boolean;
  /** Ver o comentario em `iniciarAutorizacao`. */
  dicaProvedor?: string;
  /** Ver o comentario em `AcaoNoKeycloak`. */
  acaoNoKeycloak?: AcaoNoKeycloak;
}): Promise<{ endereco: string; estado: string }> {
  const configuracao = lerConfiguracao();
  const { verificador, desafio, metodo } = await gerarDesafioPkce();
  const estado = gerarValorOpaco(16);

  guardarPedido({
    verificador,
    estado,
    destino: caminhoInternoSeguro(opcoes.destino),
  });

  const parametros = new URLSearchParams({
    response_type: 'code',
    client_id: configuracao.clienteId,
    redirect_uri: configuracao.retorno,
    scope: ESCOPOS,
    state: estado,
    code_challenge: desafio,
    code_challenge_method: metodo,
    // Exige senha mais segundo fator. Ver NIVEL_SEGUNDO_FATOR.
    acr_values: NIVEL_SEGUNDO_FATOR,
  });

  if (opcoes.silencioso) {
    parametros.set('prompt', 'none');
  }

  if (opcoes.dicaProvedor) {
    // Leva a pessoa direto a tela do provedor federado, sem passar pela tela
    // de senha do Keycloak. `acr_values` continua na URL: a dica so pula a
    // ESCOLHA de provedor, nao o nivel de autenticacao exigido.
    parametros.set('kc_idp_hint', opcoes.dicaProvedor);
  }

  if (opcoes.acaoNoKeycloak) {
    parametros.set('kc_action', opcoes.acaoNoKeycloak);
  }

  // O tema em vigor viaja junto. Ver temaEmVigor().
  parametros.set('teg_tema', temaEmVigor());

  // E as escolhas de acessibilidade, quando houver alguma. Sem nenhuma, o
  // parametro nao entra: URL de entrada nao carrega vazio.
  const acessibilidade = acessibilidadeEmVigor();
  if (acessibilidade) {
    parametros.set('teg_a11y', acessibilidade);
  }

  return { endereco: `${configuracao.autorizacao}?${parametros.toString()}`, estado };
}

/**
 * Conclui a volta do Keycloak: confere o estado e troca o codigo por token.
 *
 * Devolve o destino que estava guardado, para a aplicacao levar a pessoa de
 * volta ao que ela estava fazendo.
 */
export async function concluirAutorizacao(
  busca: string,
): Promise<{ sessao: SessaoToken; destino: string; silenciosa: boolean }> {
  const retorno = lerRetorno(busca);
  const pedido = consumirPedido();

  if (!retorno) {
    throw new FalhaAutenticacao(MENSAGEM_GENERICA, 'sem parametros de retorno');
  }

  if (retorno.erro) {
    // `login_required` de uma tentativa silenciosa e resposta esperada, nao
    // falha: significa "nao ha sessao valida, peca a entrada".
    throw new FalhaAutenticacao(mensagemDoCodigoOauth(retorno.erro), retorno.erro);
  }

  if (!pedido) {
    // Aba aberta ha muito tempo, pedido expirado, ou volta sem ida. Pedir a
    // entrada de novo e o unico caminho, e nao e erro do usuario.
    throw new FalhaAutenticacao(
      'Sua entrada demorou demais para ser concluída. Tente entrar novamente.',
      'pedido de autorizacao ausente ou expirado',
    );
  }

  if (!retorno.codigo || retorno.estado !== pedido.estado) {
    // Estado diferente do que esta aplicacao gerou: a resposta nao pertence a
    // este pedido. Nao se troca codigo nessa situacao.
    throw new FalhaAutenticacao(MENSAGEM_GENERICA, 'estado da resposta nao confere');
  }

  const configuracao = lerConfiguracao();
  const corpo = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: configuracao.clienteId,
    code: retorno.codigo,
    redirect_uri: configuracao.retorno,
    code_verifier: pedido.verificador,
  });

  const resposta = await pedirToken(configuracao.token, corpo);
  const sessao = sessaoDaResposta(resposta);

  definirSessao(sessao);
  marcarSessaoConhecida();
  limparTentativaSilenciosa();
  // O resultado de uma troca pedida ao Keycloak (kc_action), para o "Meu
  // perfil" dizer se a troca foi feita. Ver guardarResultadoDaAcao.
  guardarResultadoDaAcao(new URLSearchParams(busca).get('kc_action_status'));

  return {
    sessao,
    destino: caminhoInternoSeguro(pedido.destino),
    silenciosa: false,
  };
}

/**
 * Renovacao silenciosa.
 *
 * Enquanto a aba esta aberta, o token de renovacao (que tambem vive so em
 * memoria) troca o token de acesso vencido sem nenhuma ida ao Keycloak visivel
 * para o usuario.
 *
 * Devolve falso quando nao ha como renovar. Quem chama decide se pede a entrada
 * ou se apenas segue anonimo.
 */
export async function renovar(): Promise<boolean> {
  const atual = sessaoAtiva();

  if (!atual?.renovacao) {
    return false;
  }

  const configuracao = lerConfiguracao();
  const corpo = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: configuracao.clienteId,
    refresh_token: atual.renovacao,
  });

  try {
    const resposta = await pedirToken(configuracao.token, corpo);
    definirSessao(sessaoDaResposta(resposta));
    return true;
  } catch (erro) {
    // Token de renovacao vencido ou revogado (saida em outra aba, senha
    // trocada, sessao derrubada pela administracao). Nao e defeito.
    console.warn('Renovação de sessão recusada.', erro);
    limparSessao();
    return false;
  }
}

/**
 * Endereco de saida do Keycloak.
 *
 * `id_token_hint` diz ao Keycloak QUEM esta saindo; sem ele, ele mostra uma
 * pagina de confirmacao — passo a mais, em ingles, no meio de uma saida. O
 * `client_id` cobre o caso de nao haver token de identidade em memoria.
 */
export function enderecoDeSaida(destinoDepois: string): string {
  const configuracao = lerConfiguracao();
  const atual = sessaoAtiva();

  const parametros = new URLSearchParams({
    post_logout_redirect_uri: destinoDepois,
    client_id: configuracao.clienteId,
  });

  if (atual?.identidade) {
    parametros.set('id_token_hint', atual.identidade);
  }

  return `${configuracao.saida}?${parametros.toString()}`;
}

/** Descarta pedido pendente. Usado ao desistir da entrada. */
export function abandonarPedido(): void {
  descartarPedido();
}

interface RespostaToken {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
}

async function pedirToken(endereco: string, corpo: URLSearchParams): Promise<RespostaToken> {
  let resposta: Response;

  try {
    resposta = await fetch(endereco, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: corpo,
    });
  } catch (erro) {
    throw new FalhaAutenticacao(MENSAGEM_SEM_RESPOSTA, erro);
  }

  if (!resposta.ok) {
    let codigo: string | null = null;
    try {
      const dados = (await resposta.json()) as { error?: string };
      codigo = dados.error ?? null;
    } catch {
      codigo = null;
    }
    throw new FalhaAutenticacao(
      mensagemDoCodigoOauth(codigo),
      `ponto de token respondeu ${resposta.status}${codigo ? ` (${codigo})` : ''}`,
    );
  }

  const dados = (await resposta.json()) as Partial<RespostaToken>;

  if (!dados.access_token) {
    throw new FalhaAutenticacao(MENSAGEM_GENERICA, 'resposta de token sem access_token');
  }

  return dados as RespostaToken;
}
